interface ZoomScalePillProps {
  zoom: number;
  center?: { lng: number; lat: number };
  className?: string;
}

function getResolution(zoom: number, latitude: number): number {
  const earthCircumference = 40075016.686;
  const tileSize = 256;
  return (earthCircumference * Math.cos((latitude * Math.PI) / 180)) / (tileSize * 2 ** zoom);
}

function getScaleBar(metersPerPixel: number): { width: number; metersLabel: string } {
  const targetMeters = metersPerPixel * 28;
  const niceNumbers = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000];
  let scaleMeters = niceNumbers[0];
  for (const n of niceNumbers) {
    if (n <= targetMeters * 1.5) scaleMeters = n;
  }
  const widthPx = scaleMeters / metersPerPixel;
  const metersLabel = scaleMeters >= 1000 ? `${scaleMeters / 1000} km` : `${scaleMeters} m`;
  return { width: Math.min(widthPx, 28), metersLabel };
}

/** Compact zoom + scale pill — embed beside map controls or standalone. */
export function ZoomScalePill({ zoom, center, className = '' }: ZoomScalePillProps) {
  const latitude = center?.lat ?? 7.9;
  const scaleBar = getScaleBar(getResolution(zoom, latitude));

  return (
    <div
      className={`pointer-events-none select-none ${className}`}
      aria-live="polite"
      aria-label={`Zoom ${zoom.toFixed(1)}, ${scaleBar.metersLabel} per scale bar`}
    >
      <div className="inline-flex h-[22px] items-center gap-1 rounded-full bg-white/92 px-1.5 text-[10px] font-mono text-gray-600 shadow-sm backdrop-blur-sm dark:bg-gray-800/92 dark:text-gray-300">
        <span className="font-semibold tabular-nums text-gray-800 dark:text-gray-100">
          z{zoom.toFixed(1)}
        </span>
        <span className="h-2.5 w-px bg-gray-300/80 dark:bg-gray-600" aria-hidden />
        <span
          className="inline-block h-0.5 rounded-full bg-gray-800 dark:bg-gray-100"
          style={{ width: `${scaleBar.width}px` }}
          aria-hidden
        />
        <span className="whitespace-nowrap text-[9px] text-gray-500 dark:text-gray-400">{scaleBar.metersLabel}</span>
      </div>
    </div>
  );
}

/** @deprecated Prefer ZoomScalePill inside MapControlsFAB */
export function ZoomIndicator({ zoom, center }: ZoomScalePillProps) {
  return (
    <ZoomScalePill
      zoom={zoom}
      center={center}
      className="absolute bottom-28 right-3 z-30"
    />
  );
}
